// Mode cloud : Supabase (authentification + API REST + fonctions SQL), sans dépendance externe.
import { ApiError, type Api, type ExportData, type JobInput, type NewTask, type PlanSlot, type Session } from "./api.ts";
import type { CatalogItem, CatalogKind, DeviceAction, DeviceState, Device, Incident, Invitation, Job, Machine, Member, Operator, Organization, Role, Task, TaskEvent, TaskWithJob, DocKind, DocPage, JobDocument } from "./types.ts";

interface StoredSession { access_token: string; refresh_token: string; expires_at: number; user_id: string; email: string }

const SESSION_KEY = "ordopli-session";

function readSession(): StoredSession | null {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function messageFor(status: number, body: string): string {
  if (/^\s*</.test(body)) return `Le serveur a répondu une page au lieu de données (erreur ${status}). Rechargez la page ; si ça continue, vérifiez la configuration.`;
  try {
    const j = JSON.parse(body);
    const raw: string = j.error_description || j.msg || j.message || j.error || body;
    if (/Invalid login credentials/i.test(raw)) return "Adresse ou mot de passe incorrect.";
    if (/Email not confirmed/i.test(raw)) return "Confirmez d'abord votre adresse : un lien vous a été envoyé par mail.";
    if (/already registered/i.test(raw)) return "Un compte existe déjà avec cette adresse.";
    if (/different from the old/i.test(raw)) return "Choisissez un mot de passe différent de l'ancien.";
    if (/Password should be/i.test(raw)) return "Le mot de passe doit faire au moins 8 caractères.";
    if (/row-level security|permission denied/i.test(raw)) return "Vous n'avez pas les droits pour cette action.";
    return raw;
  } catch { return body || `Erreur ${status}`; }
}

export class CloudApi implements Api {
  readonly mode = "cloud" as const;
  private url: string;
  private key: string;
  private s: StoredSession | null = readSession();
  private orgId: string | null = null;
  private listeners = new Set<() => void>();

  constructor(url: string, anonKey: string) {
    this.url = url.trim().replace(/\/+$/, "").replace(/\/(rest|auth)\/v1$/, "");
    this.key = anonKey;
    this.readLink();
  }

  private notice: { kind: "recovery" | "confirmed" | "error"; message: string } | null = null;

  /** Les liens envoyés par Supabase (confirmation, mot de passe oublié) reviennent avec
   *  #access_token=…&type=… ou #error_description=… : on ouvre la session puis on nettoie l'adresse. */
  private readLink(): void {
    const h = typeof location !== "undefined" ? location.hash.replace(/^#\/?/, "") : "";
    if (!/(^|&)(access_token|error_description|error)=/.test(h)) return;
    const p = new URLSearchParams(h);
    const welcome = /[?&]bienvenue=1/.test(location.search);
    const target = p.get("type") === "recovery" || welcome ? "#/nouveau-mot-de-passe" : "#/planning";
    if (p.get("access_token") && p.get("refresh_token")) {
      try {
        const claims = JSON.parse(atob(p.get("access_token")!.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        this.save({ access_token: p.get("access_token")!, refresh_token: p.get("refresh_token")!, expires_in: Number(p.get("expires_in")) || 3600, user: { id: claims.sub, email: claims.email || "" } });
        this.notice = welcome
          ? { kind: "recovery", message: "Bienvenue ! Choisissez votre mot de passe pour vos prochaines connexions." }
          : p.get("type") === "recovery"
          ? { kind: "recovery", message: "Choisissez votre nouveau mot de passe." }
          : { kind: "confirmed", message: "Adresse confirmée, bienvenue sur Ordopli." };
      } catch {
        this.notice = { kind: "error", message: "Ce lien n'est pas valide. Connectez-vous ou demandez-en un nouveau." };
      }
    } else {
      const raw = p.get("error_description") || p.get("error") || "";
      this.notice = { kind: "error", message: /expired|invalid/i.test(raw) ? "Ce lien a expiré. Demandez-en un nouveau." : raw.replace(/\+/g, " ") };
    }
    const search = location.search.replace(/[?&]bienvenue=1/, "").replace(/^&/, "?");
    history.replaceState(null, "", location.pathname + search + (this.notice?.kind === "error" ? "#/connexion" : target));
  }

  /** Adresse où reviennent les liens envoyés par mail (à autoriser dans Supabase > Auth > URL Configuration). */
  private home(): string { return location.origin + location.pathname; }

  takeLinkNotice() { const n = this.notice; this.notice = null; return n; }

  async updatePassword(password: string): Promise<void> {
    const tok = await this.token();
    if (!tok) throw new ApiError("Session expirée : redemandez un lien.", 401);
    const r = await fetch(`${this.url}/auth/v1/user`, { method: "PUT", headers: { apikey: this.key, Authorization: `Bearer ${tok}`, "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
    if (!r.ok) throw new ApiError(messageFor(r.status, await r.text()), r.status);
  }

  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  private emit(): void { this.listeners.forEach((f) => f()); }

  // --- authentification -----------------------------------------------------
  session(): Session | null { return this.s ? { user_id: this.s.user_id, email: this.s.email } : null; }

  private save(d: { access_token: string; refresh_token: string; expires_in?: number; user: { id: string; email: string } }): void {
    this.s = { access_token: d.access_token, refresh_token: d.refresh_token, expires_at: Date.now() + (d.expires_in || 3600) * 1000, user_id: d.user.id, email: d.user.email };
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(this.s)); } catch { /* ignore */ }
  }

  private async auth(path: string, body: unknown): Promise<any> {
    const r = await fetch(`${this.url}/auth/v1/${path}`, { method: "POST", headers: { apikey: this.key, "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const text = await r.text();
    if (!r.ok) throw new ApiError(messageFor(r.status, text), r.status);
    return text ? JSON.parse(text) : {};
  }

  async signIn(email: string, password: string): Promise<void> {
    this.save(await this.auth("token?grant_type=password", { email, password }));
    this.orgId = null;
    this.emit();
  }

  async signUp(email: string, password: string, name: string): Promise<"connecte" | "confirmer"> {
    const d = await this.auth(`signup?redirect_to=${encodeURIComponent(this.home())}`, { email, password, data: { name } });
    if (d.access_token) { this.save(d); this.emit(); return "connecte"; }
    return "confirmer";
  }

  async resetPassword(email: string): Promise<void> {
    await this.auth(`recover?redirect_to=${encodeURIComponent(this.home())}`, { email });
  }

  async signOut(): Promise<void> {
    if (this.s) fetch(`${this.url}/auth/v1/logout`, { method: "POST", headers: { apikey: this.key, Authorization: `Bearer ${this.s.access_token}` } }).catch(() => {});
    this.s = null; this.orgId = null;
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    this.emit();
  }

  private async token(): Promise<string | null> {
    if (!this.s) return null;
    if (Date.now() < this.s.expires_at - 60_000) return this.s.access_token;
    try {
      this.save(await this.auth("token?grant_type=refresh_token", { refresh_token: this.s.refresh_token }));
      return this.s!.access_token;
    } catch {
      await this.signOut();
      return null;
    }
  }

  // --- REST -----------------------------------------------------------------
  private async rest<T = any>(method: string, path: string, body?: unknown, opts: { prefer?: string; anon?: boolean } = {}): Promise<T> {
    const headers: Record<string, string> = { apikey: this.key, "Content-Type": "application/json" };
    const tok = opts.anon ? null : await this.token();
    if (tok) headers.Authorization = `Bearer ${tok}`;
    else if (this.key.startsWith("eyJ")) headers.Authorization = `Bearer ${this.key}`;
    if (opts.prefer) headers.Prefer = opts.prefer;
    const r = await fetch(`${this.url}/rest/v1/${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    const text = await r.text();
    if (!r.ok) throw new ApiError(messageFor(r.status, text), r.status);
    return (text ? JSON.parse(text) : null) as T;
  }
  private get<T>(path: string) { return this.rest<T>("GET", path); }
  private rpc<T>(fn: string, args: unknown, anon = false) { return this.rest<T>("POST", `rpc/${fn}`, args, { anon }); }
  private async insert<T>(table: string, row: unknown): Promise<T> {
    const out = await this.rest<T[]>("POST", table, row, { prefer: "return=representation" });
    return out[0];
  }
  private async patch<T>(table: string, filter: string, values: unknown): Promise<T[]> {
    return this.rest<T[]>("PATCH", `${table}?${filter}`, values, { prefer: "return=representation" });
  }

  private org(): string {
    if (!this.orgId) throw new ApiError("Imprimerie non chargée");
    return this.orgId;
  }

  // --- imprimerie -----------------------------------------------------------
  async loadOrg(): Promise<Organization | null> {
    if (!this.s) return null;
    const q = `memberships?select=org_id,role&user_id=eq.${this.s.user_id}&order=created_at.asc&limit=1`;
    let mem = await this.get<{ org_id: string; role: Role }[]>(q);
    // invité par une imprimerie ? on la rejoint à la première connexion
    if (!mem.length && (await this.rpc<number>("accept_invitations", {}).catch(() => 0))) mem = await this.get(q);
    if (!mem.length) return null;
    this.orgId = mem[0].org_id;
    const rows = await this.get<Organization[]>(`organizations?id=eq.${this.orgId}`);
    return rows[0] ? { ...rows[0], my_role: mem[0].role } : null;
  }
  async createOrg(name: string, siret: string, userName: string): Promise<Organization> {
    const id = await this.rpc<string>("create_organization", { p_name: name, p_siret: siret, p_user_name: userName });
    this.orgId = id;
    const rows = await this.get<Organization[]>(`organizations?id=eq.${id}`);
    this.emit();
    return { ...rows[0], my_role: "admin" };
  }
  async updateOrg(patch: Partial<Pick<Organization, "name" | "siret" | "hours" | "permissions">>): Promise<void> {
    await this.patch("organizations", `id=eq.${this.org()}`, patch);
    this.emit();
  }
  async members(): Promise<Member[]> {
    return this.get<Member[]>(`memberships?org_id=eq.${this.org()}&order=created_at.asc`);
  }
  async updateMember(userId: string, role: Role): Promise<void> {
    await this.patch("memberships", `org_id=eq.${this.org()}&user_id=eq.${userId}`, { role });
    this.emit();
  }
  async removeMember(userId: string): Promise<void> {
    await this.rest("DELETE", `memberships?org_id=eq.${this.org()}&user_id=eq.${userId}`);
    this.emit();
  }
  async invitations(): Promise<Invitation[]> { return this.get(`invitations?org_id=eq.${this.org()}&order=created_at.asc`); }
  async invite(email: string, role: Role): Promise<void> {
    const e = email.trim().toLowerCase();
    await this.rest("POST", "invitations", { org_id: this.org(), email: e, role }, { prefer: "resolution=merge-duplicates" });
    this.emit();
    // lien de connexion envoyé par Supabase ; s'il échoue, la personne peut créer son compte elle-même
    await this.auth(`otp?redirect_to=${encodeURIComponent(this.home() + "?bienvenue=1")}`, { email: e, create_user: true });
  }
  async cancelInvite(email: string): Promise<void> {
    await this.rest("DELETE", `invitations?org_id=eq.${this.org()}&email=eq.${encodeURIComponent(email)}`);
    this.emit();
  }

  // --- listes -----------------------------------------------------------------
  async catalog(kind?: CatalogKind): Promise<CatalogItem[]> {
    return this.get(`catalog_items?org_id=eq.${this.org()}${kind ? `&kind=eq.${kind}` : ""}&order=position.asc,created_at.asc`);
  }
  async replaceCatalog(kind: CatalogKind, scope: string, machineId: string | null, labels: string[]): Promise<void> {
    const where = `org_id=eq.${this.org()}&kind=eq.${kind}&scope=eq.${encodeURIComponent(scope)}&machine_id=${machineId ? `eq.${machineId}` : "is.null"}`;
    await this.rest("DELETE", `catalog_items?${where}`);
    if (labels.length) {
      await this.rest("POST", "catalog_items", labels.map((label, position) => ({ org_id: this.org(), kind, scope, machine_id: machineId, label, position })));
    }
    this.emit();
  }

  // --- atelier --------------------------------------------------------------
  async machines(): Promise<Machine[]> { return this.get(`machines?org_id=eq.${this.org()}&order=position.asc,created_at.asc`); }
  async saveMachine(m: Partial<Machine> & { name: string }): Promise<Machine> {
    const { id, org_id: _o, ...values } = m as Machine;
    const row = id ? (await this.patch<Machine>("machines", `id=eq.${id}`, values))[0] : await this.insert<Machine>("machines", { ...values, org_id: this.org() });
    this.emit();
    return row;
  }
  async deleteMachine(id: string): Promise<void> { await this.rest("DELETE", `machines?id=eq.${id}`); this.emit(); }
  async operators(): Promise<Operator[]> { return this.get(`operators?org_id=eq.${this.org()}&order=name.asc`); }
  async saveOperator(o: Partial<Operator> & { name: string }, pin?: string): Promise<Operator> {
    const { id, org_id: _o, ...values } = o as Operator;
    const row = id ? (await this.patch<Operator>("operators", `id=eq.${id}`, values))[0] : await this.insert<Operator>("operators", { ...values, org_id: this.org() });
    if (pin) await this.rpc("set_operator_pin", { p_operator: row.id, p_pin: pin });
    this.emit();
    return row;
  }
  async deleteOperator(id: string): Promise<void> { await this.rest("DELETE", `operators?id=eq.${id}`); this.emit(); }

  // --- jobs -------------------------------------------------------------------
  async jobs(): Promise<Job[]> { return this.get(`jobs?org_id=eq.${this.org()}&order=due_at.asc&limit=1000`); }
  async job(id: string) {
    const [jobs, tasks] = await Promise.all([this.get<Job[]>(`jobs?id=eq.${id}`), this.get<Task[]>(`tasks?job_id=eq.${id}&order=seq.asc`)]);
    return jobs[0] ? { job: jobs[0], tasks } : null;
  }
  async saveJob(id: string | null, input: JobInput, tasks: NewTask[]): Promise<Job> {
    const job = id ? (await this.patch<Job>("jobs", `id=eq.${id}`, input))[0] : await this.insert<Job>("jobs", { ...input, org_id: this.org() });
    const existing = id ? await this.get<Task[]>(`tasks?job_id=eq.${job.id}`) : [];
    const keep = new Set(tasks.map((t) => t.id).filter(Boolean) as string[]);
    const removable = existing.filter((t) => !keep.has(t.id) && (t.status === "a_planifier" || t.status === "planifie"));
    if (removable.length) await this.rest("DELETE", `tasks?id=in.(${removable.map((t) => t.id).join(",")})`);
    for (const [i, t] of tasks.entries()) {
      const { id: tid, ...values } = t;
      if (tid && existing.some((e) => e.id === tid)) await this.patch("tasks", `id=eq.${tid}`, { ...values, seq: i + 1 });
      else await this.insert("tasks", { ...values, seq: i + 1, org_id: this.org(), job_id: job.id });
    }
    this.emit();
    return job;
  }
  async cancelJob(id: string): Promise<void> { await this.patch("jobs", `id=eq.${id}`, { status: "annule" }); this.emit(); }
  async deleteJob(id: string): Promise<void> { await this.rest("DELETE", `jobs?id=eq.${id}`); this.emit(); }

  // --- documents du job ----------------------------------------------------------
  async documents(jobId: string): Promise<JobDocument[]> {
    return this.get(`job_documents?select=id,job_id,name,kind,pages,thumb,created_at&job_id=eq.${jobId}&order=created_at.asc`);
  }
  async documentPage(docId: string, page: number): Promise<string> {
    const rows = await this.get<{ data: string }[]>(`job_document_pages?select=data&doc_id=eq.${docId}&page=eq.${page}`);
    if (!rows.length) throw new ApiError("Page introuvable", 404);
    return rows[0].data;
  }
  async addDocument(jobId: string, doc: { name: string; kind: DocKind; thumb: string; pages: DocPage[] }): Promise<JobDocument> {
    const row = await this.insert<JobDocument>("job_documents", { org_id: this.org(), job_id: jobId, name: doc.name.slice(0, 200), kind: doc.kind, thumb: doc.thumb, pages: doc.pages.length });
    try {
      // une page par requête : chaque envoi reste léger
      for (const [i, p] of doc.pages.entries()) {
        await this.rest("POST", "job_document_pages", { doc_id: row.id, org_id: this.org(), page: i + 1, data: p.data, width: p.width, height: p.height }, { prefer: "return=minimal" });
      }
    } catch (e) {
      await this.rest("DELETE", `job_documents?id=eq.${row.id}`).catch(() => {});
      throw e;
    }
    this.emit();
    return row;
  }
  async updateDocument(id: string, patch: Partial<Pick<JobDocument, "name" | "kind">>): Promise<void> {
    await this.patch("job_documents", `id=eq.${id}`, patch); this.emit();
  }
  async deleteDocument(id: string): Promise<void> { await this.rest("DELETE", `job_documents?id=eq.${id}`); this.emit(); }

  // --- planning ---------------------------------------------------------------
  async tasks(since?: Date): Promise<TaskWithJob[]> {
    const done = since ? `,and(status.eq.termine,finished_at.gte.${since.toISOString()})` : "";
    return this.get(`tasks?select=*,job:jobs(*)&org_id=eq.${this.org()}&or=(status.neq.termine${done})&limit=2000`);
  }
  async savePlan(slots: PlanSlot[]): Promise<void> {
    if (!slots.length) return;
    await this.rpc("save_plan", { p_org: this.org(), p_slots: slots });
    this.emit();
  }
  async publishPlan(): Promise<number> {
    const n = await this.rpc<number>("publish_plan", { p_org: this.org() });
    this.emit();
    return n;
  }

  // --- suivi --------------------------------------------------------------------
  async incidents(since: Date): Promise<Incident[]> {
    return this.get(`incidents?org_id=eq.${this.org()}&or=(ended_at.is.null,started_at.gte.${since.toISOString()})&order=started_at.desc&limit=500`);
  }
  async events(since: Date): Promise<TaskEvent[]> {
    return this.get(`task_events?org_id=eq.${this.org()}&at=gte.${since.toISOString()}&order=at.asc&limit=5000`);
  }

  /** Lit une table entière, page par page (le serveur renvoie au plus 1000 lignes à la fois). */
  private async all<T>(path: string): Promise<T[]> {
    const size = 1000;
    const out: T[] = [];
    for (let offset = 0; ; offset += size) {
      const page = await this.get<T[]>(`${path}&limit=${size}&offset=${offset}`);
      out.push(...page);
      if (page.length < size) return out;
    }
  }
  async exportData(): Promise<ExportData> {
    const o = this.org();
    const [jobs, tasks, incidents, events, machines, operators] = await Promise.all([
      this.all<Job>(`jobs?org_id=eq.${o}&order=created_at.asc,id.asc`),
      this.all<Task>(`tasks?org_id=eq.${o}&order=job_id.asc,seq.asc,id.asc`),
      this.all<Incident>(`incidents?org_id=eq.${o}&order=started_at.asc,id.asc`),
      this.all<TaskEvent>(`task_events?org_id=eq.${o}&order=at.asc,id.asc`),
      this.machines(),
      this.operators(),
    ]);
    return { jobs, tasks, incidents, events, machines, operators };
  }

  // --- tablettes ------------------------------------------------------------------
  async devices(): Promise<Device[]> { return this.get(`devices?select=id,org_id,machine_id,name,created_at,last_seen_at&org_id=eq.${this.org()}&order=created_at.asc`); }
  async createPairingCode(machineId: string): Promise<string> { return this.rpc<string>("create_pairing_code", { p_machine: machineId }); }
  async deleteDevice(id: string): Promise<void> { await this.rest("DELETE", `devices?id=eq.${id}`); this.emit(); }
  async pairDevice(code: string, name: string): Promise<string> {
    const r = await this.rpc<{ token: string }>("pair_device", { p_code: code.trim(), p_name: name }, true);
    return r.token;
  }
  async deviceState(token: string): Promise<DeviceState> { return this.rpc("device_state", { p_token: token }, true); }
  async deviceAction(token: string, operatorId: string, pin: string, action: DeviceAction | { type: "login" }): Promise<DeviceState> {
    return this.rpc("device_action", { p_token: token, p_operator: operatorId, p_pin: pin, p_action: action }, true);
  }
  async deviceDocuments(token: string, jobId: string): Promise<JobDocument[]> {
    return this.rpc("device_documents", { p_token: token, p_job: jobId }, true);
  }
  async deviceDocumentPage(token: string, docId: string, page: number): Promise<string> {
    return this.rpc("device_document_page", { p_token: token, p_doc: docId, p_page: page }, true);
  }
}
